"use client";

import { Component, type ReactNode } from "react";

interface BlockBoundaryProps {
	type: string;
	children: ReactNode;
}

interface State {
	error: Error | null;
}

/**
 * Per-block client error boundary. Catches render errors inside one block so
 * the rest of the page still renders. In dev, surfaces a dashed-outline marker
 * with the block type and the original error in the console; in prod, swallows
 * the failed block silently so end users see the remaining content.
 */
export class BlockBoundary extends Component<BlockBoundaryProps, State> {
	state: State = { error: null };

	static getDerivedStateFromError(error: Error): State {
		return { error };
	}

	componentDidCatch(error: Error) {
		if (process.env.NODE_ENV !== "production") {
			console.error(
				`[BlockBoundary] Block "${this.props.type}" threw:`,
				error
			);
		}
	}

	render() {
		if (this.state.error) {
			if (process.env.NODE_ENV !== "production") {
				return (
					<div
						style={{
							padding: 12,
							border: "1px dashed #c00",
							color: "#c00"
						}}
						data-block-error={this.props.type}
					>
						Block &quot;{this.props.type}&quot; failed to render.
					</div>
				);
			}
			return null;
		}
		return this.props.children;
	}
}
